import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Check, Heart, LogOut, Search } from "lucide-react";

const MAX_SELFIE_BYTES = 10 * 1024 * 1024;

// Storage key for the client's bearer token (kept locally + cookie)
const TOK_KEY = "client_token";

function LazyPhoto({ src, alt, className = "" }) {
  const holderRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const node = holderRef.current;
    if (!node) return;

    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  return (
    <div ref={holderRef} className="relative w-full h-full bg-white/5 overflow-hidden">
      {!loaded && !failed && (
        <div className="absolute inset-0 animate-pulse bg-white/10" aria-hidden="true" />
      )}
      {visible && src ? (
        <img
          src={src}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setFailed(true);
            setLoaded(false);
          }}
        />
      ) : null}
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center px-3 text-center text-[11px] text-neutral-500">
          Photo could not be loaded
        </div>
      )}
    </div>
  );
}


export default function ClientPortal() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [me, setMe] = useState(undefined);
  const [scanning, setScanning] = useState(true);

  useEffect(() => {
    const scanToken = params.get("token");
    const boot = async () => {
      // If a token is present in URL, exchange it (auto-login)
      if (scanToken) {
        try {
          const fd = new FormData();
          fd.append("token", scanToken);
          const { data } = await api.post("/client/scan", fd);
          localStorage.setItem(TOK_KEY, data.token);
          // Set X-Client-Token header for subsequent requests
          api.defaults.headers.common["X-Client-Token"] = data.token;
          setMe({ event: data.event, booking: data.booking, access_mode: data.access_mode || "guest" });
          nav("/client", { replace: true });
          setScanning(false);
          return;
        } catch (err) {
          toast.error(formatApiError(err));
          setMe(null);
          setScanning(false);
          return;
        }
      }
      const stored = localStorage.getItem(TOK_KEY);
      if (stored) api.defaults.headers.common["X-Client-Token"] = stored;
      try {
        const { data } = await api.get("/client/me");
        setMe(data);
      } catch {
        setMe(null);
      } finally {
        setScanning(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/client/logout");
    } catch {}
    delete api.defaults.headers.common["X-Client-Token"];
    localStorage.removeItem(TOK_KEY);
    setMe(null);
  };

  if (scanning) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-neutral-500 text-sm">Loading portal…</div>
      </div>
    );
  }

  if (!me || !me.event) {
    return (
      <div
        data-testid="client-portal-no-session"
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6"
      >
        <div className="max-w-md text-center">
          <div className="overline mb-4">Client Portal</div>
          <h1 className="font-serif-display text-4xl mb-6">
            Scan your QR to enter.
          </h1>
          <p className="text-neutral-400 text-sm">
            Your photographer sent you a personal QR code — scan it with your
            phone to view and select your event photos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="client-portal" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 px-6 md:px-10 py-6 flex justify-between items-center">
        <div>
          <div className="font-serif-display text-xl">
            Wedding<span className="text-[#E11D2A]">·</span>Touch
          </div>
          <div className="overline mt-1 text-neutral-500">
            {me.event.title} · {me.booking?.client_name}
          </div>
        </div>
        <button
          data-testid="client-logout"
          onClick={logout}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-white"
        >
          <LogOut size={14} /> Sign out
        </button>
      </header>

      <ClientBody accessMode={me.access_mode || "guest"} />
    </div>
  );
}

function ClientBody({ accessMode }) {
  const albumOnly = accessMode === "album";
  const [tab, setTab] = useState(albumOnly ? "album" : "selfie");
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
      <div className="flex gap-2 mb-8 border-b border-white/10">
        {!albumOnly && (
          <TabBtn
            testId="tab-selfie"
            active={tab === "selfie"}
            onClick={() => setTab("selfie")}
            icon={<Search size={14} />}
          >
            Find my photos
          </TabBtn>
        )}
        {albumOnly && (
          <TabBtn
            testId="tab-album"
            active={true}
            onClick={() => setTab("album")}
            icon={<Heart size={14} />}
          >
            Album selection
          </TabBtn>
        )}
      </div>
      {albumOnly ? <AlbumSelection /> : <SelfieMatcher />}
    </div>
  );
}

function TabBtn({ active, onClick, children, icon, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-3 text-sm uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 transition-colors -mb-px ${
        active
          ? "border-[#E11D2A] text-white"
          : "border-transparent text-neutral-500 hover:text-white"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function SelfieMatcher() {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_SELFIE_BYTES) {
      toast.error("Selfie must be under 10MB");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const search = async () => {
    if (!file) return toast.error("Take or pick a selfie first");
    setSearching(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/client/me/photos/search", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.matches.length === 0) {
        toast.info("No matches yet — try a clearer selfie");
      } else {
        toast.success(`Found ${data.matches.length} photo(s) of you`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="selfie-matcher">
      <div className="mb-6">
        <div className="overline mb-2">Step 1</div>
        <h2 className="font-serif-display text-3xl">
          Take a clear selfie to find <em>your</em> photos.
        </h2>
        <p className="text-neutral-400 text-sm mt-2 max-w-xl">
          Face the camera in bright light. We'll match your face against all the
          event photos privately — nothing is shared with anyone else.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div>
          <input
            ref={inputRef}
            data-testid="selfie-input"
            type="file"
            accept="image/*"
            capture="user"
            onChange={pick}
            className="hidden"
          />
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="selfie-pick"
            className="w-full aspect-square border-2 border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center gap-3 text-neutral-400 hover:text-white transition-colors"
          >
            {preview ? (
              <img
                src={preview}
                alt="selfie"
                className="w-full h-full object-cover"
              />
            ) : (
              <>
                <Camera size={40} strokeWidth={1} />
                <div className="text-sm uppercase tracking-widest">
                  Take / choose selfie
                </div>
              </>
            )}
          </button>
          <button
            onClick={search}
            disabled={!file || searching}
            data-testid="selfie-search"
            className="mt-4 w-full bg-white text-black py-4 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
          >
            {searching ? "Searching…" : "Find my photos"}
          </button>
        </div>

        <div>
          {result ? (
            <>
              <div className="overline mb-4">
                {result.matches.length} match
                {result.matches.length === 1 ? "" : "es"} · scanned{" "}
                {result.total_faces_scanned} face(s)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
  {result.matches.map((m) => (
    <div
      key={m.id}
      data-testid={`match-${m.id}`}
      className="border border-white/10 overflow-hidden bg-[#0c0c0c]"
    >
      {/* Photo */}
      <div className="aspect-square overflow-hidden">
        <LazyPhoto
          src={m.image_url}
          alt={m.filename || "Wedding photo"}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Download only */}
      <div className="p-3">
        <button
          type="button"
          onClick={async () => {
            try {
              const response = await fetch(m.image_url);

              if (!response.ok) {
                throw new Error("Download failed");
              }

              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);

              const link = document.createElement("a");
              link.href = url;
              link.download = m.filename || "wedding-photo.jpg";

              document.body.appendChild(link);
              link.click();
              link.remove();

              window.URL.revokeObjectURL(url);
            } catch (err) {
              console.error("Download failed:", err);
              toast.error("Could not download photo");
            }
          }}
          className="w-full bg-white text-black py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#E11D2A] hover:text-white transition-colors"
        >
          Download
        </button>
      </div>
    </div>
  ))}
</div>
              
            </>
          ) : (
            <div className="border border-white/10 p-8 rounded-sm text-sm text-neutral-500">
              Matches will appear here after you scan your selfie.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlbumSelection() {
  const [photos, setPhotos] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, a] = await Promise.all([
        api.get("/client/me/photos"),
        api.get("/client/me/album"),
      ]);
      setPhotos(p.data);
      setPicked(new Set(a.data.photo_ids || []));
      setSubmitted(!!a.data.submitted);
      setLoading(false);
    })();
  }, []);

  const toggle = (id) => {
    const s = new Set(picked);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setPicked(s);
    setSubmitted(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/client/me/album", {
        photo_ids: Array.from(picked),
      });
      toast.success(`Sent ${picked.size} selections to your photographer`);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;

  return (
    <div data-testid="album-selection">
      <div className="flex justify-between items-end flex-wrap gap-4 mb-6">
        <div>
          <div className="overline mb-2">Album selection</div>
          <h2 className="font-serif-display text-3xl">
            Tap the photos you love for your album.
          </h2>
          <p className="text-neutral-400 text-sm mt-2">
            {picked.size} selected · {photos.length} total
          </p>
        </div>
        <button
          data-testid="album-save"
          onClick={save}
          disabled={saving || picked.size === 0}
          className="bg-white text-black px-8 py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50 flex items-center gap-2"
        >
          {submitted && <Check size={14} />}
          {saving
            ? "Sending…"
            : submitted
            ? "Selections saved"
            : "Send to photographer"}
        </button>
      </div>

      {photos.length === 0 ? (
        <div className="text-neutral-500 text-sm border border-white/10 p-8 rounded-sm">
          Photos will appear here once your photographer uploads them.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => {
            const on = picked.has(p.id);
            return (
              <button
                key={p.id}
                data-testid={`album-photo-${p.id}`}
                onClick={() => toggle(p.id)}
                className={`relative aspect-square overflow-hidden border-2 transition-all ${
                  on
                    ? "border-[#E11D2A] scale-[0.98]"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
               <LazyPhoto
                    src={p.image_url}
                    alt={p.filename || "Wedding photo"}
                    className="w-full h-full object-cover"
                  />
                {on && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-[#E11D2A] flex items-center justify-center">
                      <Check size={20} />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
